import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LoginRequest, LoginResponse, AuthUser, GoogleLoginRequest } from '../models/auth.model';
import { Router } from '@angular/router';
import { PermissionsService } from './permissions.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly TOKEN_KEY = 'bdl_token';
  private readonly USER_KEY = 'bdl_user';
  private readonly SESSION_TIMEOUT_KEY = 'bdl_session_timeout';
  private currentUserSubject = new BehaviorSubject<AuthUser | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  private sessionTimeoutId?: number;
  private sessionWarningId?: number;
  private permissionsService?: PermissionsService;

  constructor(
    private http: HttpClient,
    private router: Router,
    private injector: Injector
  ) {
    this.loadUserFromStorage();
    this.checkSessionTimeout();
    // Load permissions if user is already logged in (use setTimeout to avoid circular dependency)
    setTimeout(() => {
      this.loadPermissionsIfAuthenticated();
    }, 0);
  }

  // Lazy getter for PermissionsService to avoid circular dependency
  private getPermissionsService(): PermissionsService {
    if (!this.permissionsService) {
      this.permissionsService = this.injector.get(PermissionsService);
    }
    return this.permissionsService;
  }

  login(credentials: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${environment.apiUrl}/auth/login`, credentials)
      .pipe(
        tap(response => {
          this.setToken(response.access_token);
          this.setUser(response.user);
          this.startSessionTimeout();
          // Load permissions after login
          this.loadUserPermissions();
        })
      );
  }

  googleLogin(googleData: GoogleLoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${environment.apiUrl}/auth/google-login`, googleData)
      .pipe(
        tap(response => {
          this.setToken(response.access_token);
          this.setUser(response.user);
          this.startSessionTimeout();
          // Load permissions after login
          this.loadUserPermissions();
        })
      );
  }

  logout(): void {
    // Clear permissions cache on logout
    try {
      this.getPermissionsService().clearCache();
    } catch (error) {
      // Ignore if PermissionsService is not available
      console.warn('Could not clear permissions cache:', error);
    }
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.SESSION_TIMEOUT_KEY);
    this.currentUserSubject.next(null);
    this.clearSessionTimeout();
    this.clearSessionWarning();
    this.router.navigate(['/login']);
  }

  // Force logout without navigation (for session expiration)
  forceLogout(): void {
    // Clear permissions cache on logout
    try {
      this.getPermissionsService().clearCache();
    } catch (error) {
      // Ignore if PermissionsService is not available
      console.warn('Could not clear permissions cache:', error);
    }
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.SESSION_TIMEOUT_KEY);
    this.currentUserSubject.next(null);
    this.clearSessionTimeout();
    this.clearSessionWarning();
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  getCurrentUser(): AuthUser | null {
    return this.currentUserSubject.value;
  }

  isAdmin(): boolean {
    // Admin protection disabled - all users are now admins
    return true;
  }

  isApprover(): boolean {
    const user = this.getCurrentUser();
    return user?.userCompanies?.some(uc => uc.aprobador) || false;
  }

  // Handle session expiration from server response
  handleSessionExpiration(): void {
    try {
      this.forceLogout();
      this.router.navigate(['/login']);
    } catch (error) {
      console.error('Error handling session expiration:', error);
      // Fallback: just clear storage without navigation
      this.forceLogout();
    }
  }

  // Refresh session timeout
  refreshSession(): void {
    try {
      this.startSessionTimeout();
    } catch (error) {
      console.error('Error refreshing session:', error);
    }
  }

  private setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  private setUser(user: AuthUser): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    this.currentUserSubject.next(user);
  }

  private loadUserFromStorage(): void {
    try {
      const userJson = localStorage.getItem(this.USER_KEY);
      if (!userJson) return;
      const user = JSON.parse(userJson);
      if (user && typeof user === 'object') {
        this.currentUserSubject.next(user);
      }
    } catch (error) {
      console.error('Stored user payload is invalid; continuing without it', error);
      localStorage.removeItem(this.USER_KEY);
    }
  }

  private startSessionTimeout(): void {
    this.clearSessionTimeout();
    this.clearSessionWarning();

    // Set session timeout to 8 hours (28800000 ms)
    const sessionTimeout = 8 * 60 * 60 * 1000;
    const warningTime = 7.5 * 60 * 60 * 1000; // Warning 30 minutes before expiration
    const timeoutTime = Date.now() + sessionTimeout;

    localStorage.setItem(this.SESSION_TIMEOUT_KEY, timeoutTime.toString());

    // Set warning timeout
    this.sessionWarningId = window.setTimeout(() => {
      this.showSessionWarning();
    }, warningTime);

    // Set session expiration timeout
    this.sessionTimeoutId = window.setTimeout(() => {
      this.handleSessionExpiration();
    }, sessionTimeout);
  }

  private clearSessionTimeout(): void {
    if (this.sessionTimeoutId) {
      clearTimeout(this.sessionTimeoutId);
      this.sessionTimeoutId = undefined;
    }
  }

  private clearSessionWarning(): void {
    if (this.sessionWarningId) {
      clearTimeout(this.sessionWarningId);
      this.sessionWarningId = undefined;
    }
  }

  private showSessionWarning(): void {
    // Show a browser notification or alert about session expiration
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Session Expiring Soon', {
        body: 'Your session will expire in 30 minutes. Please save your work.',
        icon: '/assets/icons/icon-192x192.png'
      });
    }

    // You can also show a toast notification here if you have a notification service
    console.warn('Session will expire in 30 minutes');
  }

  private checkSessionTimeout(): void {
    const timeoutTime = localStorage.getItem(this.SESSION_TIMEOUT_KEY);
    if (timeoutTime) {
      const timeout = parseInt(timeoutTime);
      const now = Date.now();

      if (now >= timeout) {
        // Session has expired
        this.handleSessionExpiration();
      } else {
        // Session is still valid, restart timeout
        const remainingTime = timeout - now;
        const warningTime = Math.max(0, remainingTime - (30 * 60 * 1000)); // 30 minutes before expiration

        // Clear existing timeouts first to prevent duplicates
        this.clearSessionTimeout();
        this.clearSessionWarning();

        // Set warning timeout
        if (warningTime > 0) {
          this.sessionWarningId = window.setTimeout(() => {
            this.showSessionWarning();
          }, warningTime);
        }

        // Set session expiration timeout
        this.sessionTimeoutId = window.setTimeout(() => {
          this.handleSessionExpiration();
        }, remainingTime);
      }
    }
  }

  // Add method to safely check if service is ready
  isServiceReady(): boolean {
    try {
      return this.currentUserSubject !== null &&
             typeof this.getToken === 'function' &&
             typeof this.isAuthenticated === 'function';
    } catch (error) {
      console.error('Auth service not ready:', error);
      return false;
    }
  }

  // Load user permissions after login
  private loadUserPermissions(): void {
    const user = this.getCurrentUser();
    if (!user) {
      return;
    }

    const email = user.username || user.email || '';
    const isRindegastos = email && email.endsWith('@rindegastos.com');

    try {
      const permissionsService = this.getPermissionsService();
      
      // For rindegastos users, load permissions by email
      if (isRindegastos) {
        permissionsService.loadUserPermissionsByEmail().subscribe({
          next: (permissions) => {
            console.log('Loaded permissions for user:', email, permissions?.length ?? 0, 'modules');
          },
          error: (error) => {
            console.error('Error loading permissions by email:', error);
          }
        });
      } else if (user.id) {
        // For other users, load by user ID
        permissionsService.loadUserPermissions().subscribe({
          next: (permissions) => {
            console.log('Loaded permissions for user:', user.id, permissions?.length ?? 0, 'modules');
          },
          error: (error) => {
            console.error('Error loading permissions:', error);
          }
        });
      }
    } catch (error) {
      // Ignore if PermissionsService is not available (circular dependency protection)
      console.warn('Could not load permissions:', error);
    }
  }

  // Load permissions if user is already authenticated (e.g., on page refresh)
  private loadPermissionsIfAuthenticated(): void {
    if (this.isAuthenticated()) {
      this.loadUserPermissions();
    }
  }
}
